import type {
  DocType,
  UesFieldDef,
} from "../domain/entities/doc-type";
import type { FieldPath } from "./schema-editor-ops";

export const KEY_REGEX = /^[a-z][a-z0-9_]*$/;

export interface FieldError {
  path: FieldPath;
  field: "name" | "label" | "description" | "enumValues" | "fields";
  message: string;
}

export interface DocTypeErrors {
  key?: string;
  displayName?: string;
  description?: string;
  fields: FieldError[];
}

type DocTypeValidationInput = Pick<
  DocType,
  "key" | "displayName" | "description" | "uesFields"
>;

const MAX_NAME_LENGTH = 30;
const MAX_DESCRIPTION_WORDS = 100;

export function wordCount(text: string): number {
  return text.trim().split(/\s+/).filter(Boolean).length;
}

export const MAX_FIELD_DESCRIPTION_WORDS = MAX_DESCRIPTION_WORDS;

function validateFields(
  fields: UesFieldDef[],
  parentPath: FieldPath,
  depth: number,
  errors: FieldError[],
): void {
  const seenNames = new Set<string>();

  fields.forEach((field, index) => {
    const path = [...parentPath, index];

    if (!field.name.trim()) {
      errors.push({ path, field: "name", message: "name is required" });
    } else if (field.name.length > MAX_NAME_LENGTH) {
      errors.push({
        path,
        field: "name",
        message: "name exceeds 30 characters",
      });
    }

    if (seenNames.has(field.name)) {
      errors.push({
        path,
        field: "name",
        message: "sibling field name must be unique",
      });
    }
    seenNames.add(field.name);

    if (!field.label.trim()) {
      errors.push({ path, field: "label", message: "label is required" });
    } else if (field.label.length > MAX_NAME_LENGTH) {
      errors.push({
        path,
        field: "label",
        message: "label exceeds 30 characters",
      });
    }

    if (!field.description.trim()) {
      errors.push({
        path,
        field: "description",
        message: "description is required",
      });
    } else if (wordCount(field.description) > MAX_DESCRIPTION_WORDS) {
      errors.push({
        path,
        field: "description",
        message: "description exceeds 100 words",
      });
    }

    if (field.type === "enum" && !field.enumValues?.length) {
      errors.push({
        path,
        field: "enumValues",
        message: "enum values are required",
      });
    }

    if (field.type === "object") {
      if (depth >= 2) {
        errors.push({
          path,
          field: "fields",
          message: "nesting exceeds depth 2",
        });
      }
      if (!field.fields?.length) {
        errors.push({
          path,
          field: "fields",
          message: "object fields are required",
        });
      } else {
        validateFields(field.fields, path, depth + 1, errors);
      }
    }
  });
}

export function validateDocType(
  input: DocTypeValidationInput,
  opts: { isCreate: boolean },
): DocTypeErrors {
  const errors: DocTypeErrors = { fields: [] };

  if (opts.isCreate && !KEY_REGEX.test(input.key)) {
    errors.key = "key must be snake_case (lowercase, digits, underscore)";
  }

  if (!input.displayName.trim()) {
    errors.displayName = "display name is required";
  } else if (input.displayName.length > MAX_NAME_LENGTH) {
    errors.displayName = "display name exceeds 30 characters";
  }

  if (!input.description.trim()) {
    errors.description = "description is required";
  } else if (wordCount(input.description) > MAX_DESCRIPTION_WORDS) {
    errors.description = "description exceeds 100 words";
  }

  validateFields(input.uesFields, [], 1, errors.fields);
  return errors;
}

export function isValid(errors: DocTypeErrors): boolean {
  return (
    errors.key === undefined &&
    errors.displayName === undefined &&
    errors.description === undefined &&
    errors.fields.length === 0
  );
}
